import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "@src/store/store";
import { Event, PAGINATION_CONFIG } from "../../pages/home/children/eventlist/EventList.constants";
import { ApiProvider } from "@src/store/ApiProvider";

export interface EventFilterState {
	data: Array<Event>;
	loading: boolean;
	error: string | null;
	sortBy: string | null;
	sortDirection: "asc" | "desc" | null;
	pageNo: number;
	pageSize: number;
	totalPages: number;
	smartSearch: string | null;
	eventName: string | null;
	artistName: string | null;
	venueName: string | null;
	state: string | null;
	city: string | null;
	presaleStartTime: Date | null;
	presaleEndTime: Date | null;
	presaleStartDateTime: Date | null;
	presaleEndDateTime: Date | null;
	eventCategory: string | null;
	eventStartDate: Date | null;
	eventEndDate: Date | null;
	eventStatus: string | null;
	capacityRange: Array<number | null | undefined> | null | undefined;
	aiRange: Array<number | null | undefined> | null | undefined;
}

const initialState: EventFilterState = {
	data: [],
	loading: false,
	error: null,
	sortBy: null,
	sortDirection: null,
	pageNo: 1,
	pageSize: PAGINATION_CONFIG.PAGE_RECORDS,
	totalPages: 1,
	smartSearch: null,
	eventName: null,
	artistName: null,
	venueName: null,
	state: null,
	city: null,
	presaleStartTime: null,
	presaleEndTime: null,
	presaleStartDateTime: null,
	presaleEndDateTime: null,
	eventCategory: null,
	eventStartDate: null,
	eventEndDate: null,
	eventStatus: null,
	capacityRange: [null, null],
	aiRange: [null, null],
};

export const submitEventFilter = createAsyncThunk<{ data: Array<Event>; totalPages: number }, Partial<EventFilterState>>(
	"eventFilter/submitEventFilter",
	async (params = {}, { getState, rejectWithValue }) => {
		// Get dynamic values from filters or settings
		const state = getState() as RootState;
		const {
			sortBy,
			sortDirection,
			pageNo = 1,
			pageSize = PAGINATION_CONFIG.PAGE_RECORDS,
			smartSearch,
			eventName,
			artistName,
			venueName,
			state: eventState,
			city,
			presaleStartTime,
			presaleEndTime,
			presaleStartDateTime,
			presaleEndDateTime,
			eventCategory,
			eventStartDate,
			eventEndDate,
			eventStatus,
			capacityRange,
			aiRange,
		} = { ...state.events.filter, ...params };

		const timeZone = (() => {
			try {
				return Intl.DateTimeFormat().resolvedOptions().timeZone;
			} catch (error) {
				return rejectWithValue("Failed to detect timezone.");
			}
		})();

		const formatDate = (date: Date | null | undefined) => {
			if (!date) return null;
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		};

		// Format a Date object as 'HH:mm:ss' (24-hour format)
		const formatTime = (date: Date | null | undefined) => {
			if (!date) return null;
			if (Number.isNaN(date.getTime())) return null;
			const hours = String(date.getHours()).padStart(2, "0");
			const minutes = String(date.getMinutes()).padStart(2, "0");
			const seconds = String(date.getSeconds()).padStart(2, "0");
			return `${hours}:${minutes}:${seconds}`;
		};

		const timeZoneId = timeZone;
		// Map and format the request body as required by the API
		const requestBody = {
			page: pageNo,
			size: pageSize,
			timeZoneId,
			sortBy,
			sortDirection,
			smartSearch,
			eventName: eventName,
			venue: venueName,
			category: eventCategory,
			artist: artistName,
			eventStartDate: eventStartDate ? formatDate(eventStartDate) : null,
			eventEndDate: eventEndDate ? formatDate(eventEndDate) : null,
			status: eventStatus,
			state: eventState,
			city,
			presaleStartDateTime: formatDate(presaleStartDateTime),
			presaleEndDateTime: formatDate(presaleEndDateTime),
			presaleStartTime: formatTime(presaleStartTime),
			presaleEndTime: formatTime(presaleEndTime),
			aiRange: Array.isArray(aiRange) && aiRange.every((v) => v == null || v === 0) ? null : aiRange,
			capacityRange: Array.isArray(capacityRange) && capacityRange.every((v) => v == null || v === 0) ? null : capacityRange,
		};

		interface EventSearchResponse {
			events?: Array<Event>;
			totalCount?: number;
			message?: string;
		}

		let result: EventSearchResponse;
		try {
			// Routed through the portal backend proxy, which forwards the portal JWT to internal-api.
			result = await ApiProvider.default.post<EventSearchResponse>("/api/events/search", requestBody);
		} catch (err: unknown) {
			// 401s are handled by the ApiProvider response interceptor (auth:session-expired).
			// Surface a user-friendly message for other errors.
			const msg = axios.isAxiosError(err) ? err.response?.data?.message || err.message : "Failed to fetch events";
			return rejectWithValue(msg);
		}

		if (!Array.isArray(result.events)) {
			const errorMessage = result?.message || "No events found in the response.";
			return rejectWithValue(errorMessage);
		}

		const events = result.events;
		const totalPages =
			typeof result.totalCount === "number" ? Math.ceil(result.totalCount / PAGINATION_CONFIG.PAGE_RECORDS) : 1;
		return { data: events, totalPages };
	},
);

export const eventFilterSlice = createSlice({
	name: "eventFilter",
	initialState,
	reducers: {
		setSortFilter: (state, action: PayloadAction<{ sortBy: string | null; sortDirection: "asc" | "desc" | null }>) => {
			state.sortBy = action.payload.sortBy;
			state.sortDirection = action.payload.sortDirection;
			state.pageNo = 1;
		},
		setPageFilter: (state, action: PayloadAction<{ pageNo: number }>) => {
			state.pageNo = action.payload.pageNo;
		},
		setSearchFilter: (
			state,
			action: PayloadAction<Partial<Omit<EventFilterState, "data" | "loading" | "error" | "totalPages">>>,
		) => {
			Object.assign(state, action.payload);
			state.pageNo = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(submitEventFilter.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(submitEventFilter.fulfilled, (state, action) => {
				state.loading = false;
				state.data = action.payload.data;
				state.totalPages = action.payload.totalPages;
			})
			.addCase(submitEventFilter.rejected, (state, action) => {
				if (action.meta.aborted) return;
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to fetch events";
				state.data = [];
				state.pageNo = 1;
				state.totalPages = 1;
			});
	},
});

export const { setSortFilter, setPageFilter, setSearchFilter } = eventFilterSlice.actions;
export default eventFilterSlice.reducer;
